import { SettingOutlined, WarningOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Flex, Form, InputNumber, Modal, Select, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { PlatformLabel } from "../channels/PlatformBadge";
import { rupiahInput } from "../procurement/common";

type Row = Schemas["PriceMarginRow"];
type Profile = Schemas["CostProfile"];
const STATUS_CHOICES = ["all", "below_break_even", "below_target", "no_cost", "unmapped"] as const;
const ROUND_CHOICES = ["100", "500", "1000"] as const;

/** Keuangan > Margin harga (Plan §6.20): is today's price still making money, and what price reaches the target. */
export function Margins() {
  const { t, formatMoney, formatNumber } = useI18n();
  const { can } = useAuth();
  const status = useStatusFilter(STATUS_CHOICES, "all");
  const search = useSearchFilter();
  const page = useListPage();
  const [connectionId, setConnectionId] = useUrlFilter<string>("connectionId", "");
  const [roundTo, setRoundTo] = useUrlFilter<(typeof ROUND_CHOICES)[number]>("roundTo", "100", ROUND_CHOICES);
  const [editing, setEditing] = useState<Profile | null>(null);
  const pct = (bp: number) => `${formatNumber(bp / 100)}%`;

  const profiles = useQuery({ queryKey: ["finance", "cost-profiles"], queryFn: () => unwrap(api.GET("/v1/finance/cost-profiles")) });
  const filters = {
    ...(status.value !== "all" ? { status: status.value } : {}),
    ...(connectionId ? { connectionId } : {}),
    ...(roundTo !== "100" ? { roundTo: Number(roundTo) as 500 | 1000 } : {}),
    ...search.query,
  };
  const list = useQuery({
    queryKey: ["finance", "price-margins", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/finance/price-margins", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  const below = list.data?.belowBreakEven ?? [];
  const money = (v: number | null) => (v == null ? <Typography.Text type="secondary">—</Typography.Text> : <span className="z-num">{formatMoney(v)}</span>);

  return (
    <>
      <Typography.Title level={2} style={{ margin: "0 0 4px", fontSize: 28 }}>
        {t("margin.title")}
      </Typography.Title>
      <Typography.Paragraph type="secondary">{t("margin.subtitle")}</Typography.Paragraph>
      {below.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("margin.belowAlert")}
          description={
            <Space wrap>
              {below.map((b) => (
                <Button
                  key={b.connectionId}
                  size="small"
                  onClick={() => {
                    setConnectionId(b.connectionId);
                    status.set("below_break_even");
                  }}
                >
                  {b.name} · {t("margin.skus", { n: formatNumber(b.skus) })}
                </Button>
              ))}
            </Space>
          }
        />
      )}
      <Card size="small" title={t("margin.profiles")} style={{ marginBottom: 16 }} loading={profiles.isPending}>
        <Table<Profile>
          size="small"
          rowKey="connectionId"
          pagination={false}
          dataSource={profiles.data?.items}
          locale={{ emptyText: t("margin.noStores") }}
          columns={[
            {
              title: t("orders.store"),
              render: (_, p) => (
                <Space orientation="vertical" size={0}>
                  <PlatformLabel platform={p.platform} />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {p.name}
                  </Typography.Text>
                </Space>
              ),
            },
            { title: t("margin.fee"), width: 130, align: "right", render: (_, p) => <span className="z-num">{pct(p.feeBp)}</span> },
            { title: t("margin.fixedFee"), width: 140, align: "right", render: (_, p) => money(p.fixedFee) },
            { title: t("margin.ads"), width: 100, align: "right", render: (_, p) => <span className="z-num">{pct(p.adsBp)}</span> },
            { title: t("margin.target"), width: 120, align: "right", render: (_, p) => <span className="z-num">{pct(p.marginBp)}</span> },
            {
              title: t("margin.realFee"),
              width: 200,
              render: (_, p) =>
                p.realFeeBp == null ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t("margin.realFeeNone", { n: formatNumber(p.settledOrders) })}
                  </Typography.Text>
                ) : (
                  <span className="z-num">{pct(p.realFeeBp)}</span>
                ),
            },
            {
              width: 110,
              render: (_, p) => (
                <Space>
                  {!p.saved && <Tag>{t("margin.default")}</Tag>}
                  {can("finance.manage") && (
                    <Button size="small" icon={<SettingOutlined />} onClick={() => setEditing(p)} aria-label={t("margin.edit", { store: p.name })} />
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("margin.search")} />
        <StatusFilter filter={status} width={220} options={STATUS_CHOICES.map((s) => ({ value: s, label: t(`margin.filter.${s}`) }))} />
        <Select
          value={connectionId}
          onChange={setConnectionId}
          style={{ width: 220 }}
          options={[{ value: "", label: t("price.storesAll") }, ...(profiles.data?.items ?? []).map((p) => ({ value: p.connectionId, label: p.name }))]}
        />
        <Select
          value={roundTo}
          onChange={setRoundTo}
          style={{ width: 180 }}
          aria-label={t("price.round")}
          options={ROUND_CHOICES.map((n) => ({ value: n, label: t("price.round.to", { n: Number(n).toLocaleString("id-ID") }) }))}
        />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/finance/price-margins/export"
          query={filters}
          filename={t("margin.title")}
          columns={[
            { key: "shopName", label: t("orders.store") },
            { key: "skuCode", label: t("catalog.code") },
            { key: "sellerSku", label: t("margin.sellerSku") },
            { key: "listingName", label: t("catalog.platform.product") },
            { key: "price", label: t("margin.price"), kind: "number" },
            { key: "unitCost", label: t("margin.cost"), kind: "number" },
            { key: "deduction", label: t("margin.deduction"), kind: "number" },
            { key: "margin", label: t("margin.margin"), kind: "number" },
            { key: "breakEven", label: t("margin.breakEven"), kind: "number" },
            { key: "suggested", label: t("margin.suggested"), kind: "number" },
          ]}
        />
      </Flex>
      <Table<Row>
        rowKey="channelSkuId"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        scroll={{ x: 1100 }}
        locale={{ emptyText: t("margin.empty") }}
        columns={[
          {
            title: t("orders.store"),
            width: 170,
            render: (_, r) => (
              <Space orientation="vertical" size={0}>
                <PlatformLabel platform={r.platform} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.shopName}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t("catalog.code"),
            render: (_, r) => (
              <span>
                <span className="z-num">{r.skuCode || r.sellerSku}</span>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {[r.listingName, r.variant].filter(Boolean).join(" · ")}
                </Typography.Text>
              </span>
            ),
          },
          { title: t("margin.price"), dataIndex: "price", width: 120, align: "right", render: money },
          {
            title: t("margin.cost"),
            width: 130,
            align: "right",
            render: (_, r) =>
              r.problem ? (
                <Tag icon={<WarningOutlined />} color="warning">
                  {t(`margin.problem.${r.problem}`)}
                </Tag>
              ) : (
                money(r.unitCost)
              ),
          },
          { title: t("margin.deduction"), dataIndex: "deduction", width: 130, align: "right", render: money },
          {
            title: t("margin.margin"),
            width: 140,
            align: "right",
            render: (_, r) =>
              r.margin == null ? (
                money(null)
              ) : (
                <Space orientation="vertical" size={0} align="end">
                  <Typography.Text className="z-num" type={r.margin < 0 ? "danger" : (r.marginBp ?? 0) < r.targetMarginBp ? "warning" : undefined}>
                    {formatMoney(r.margin)}
                  </Typography.Text>
                  {r.marginBp != null && (
                    <Typography.Text type="secondary" className="z-num" style={{ fontSize: 12 }}>
                      {pct(r.marginBp)}
                    </Typography.Text>
                  )}
                </Space>
              ),
          },
          { title: t("margin.breakEven"), dataIndex: "breakEven", width: 130, align: "right", render: money },
          {
            title: t("margin.suggested"),
            dataIndex: "suggested",
            width: 140,
            align: "right",
            render: (v: number | null) => (v == null ? money(null) : <strong className="z-num">{formatMoney(v)}</strong>),
          },
        ]}
      />
      <ProfileDialog profile={editing} onClose={() => setEditing(null)} />
    </>
  );
}

type ProfileForm = { fee: number; fixedFee: number; ads: number; margin: number };

/** One store's cost profile, in percent on screen and basis points on the wire. */
function ProfileDialog({ profile, onClose }: { profile: Profile | null; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ProfileForm>();
  const bp = (v: number | undefined) => Math.round((v ?? 0) * 100);
  const save = useMutation({
    mutationFn: (v: ProfileForm) =>
      unwrap(
        api.PUT("/v1/finance/cost-profiles/{connectionID}", {
          params: { path: { connectionID: profile!.connectionId } },
          body: { feeBp: bp(v.fee), fixedFee: v.fixedFee ?? 0, adsBp: bp(v.ads), marginBp: bp(v.margin) },
        }),
      ),
    onSuccess: () => {
      void message.success(t("margin.saved"));
      void queryClient.invalidateQueries({ queryKey: ["finance"] });
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err, { feeBp: "fee", adsBp: "ads", marginBp: "margin" })) void message.error(errorText(err));
    },
  });
  const percent = { min: 0, max: 100, precision: 2, suffix: "%", style: { width: "100%" } };

  return (
    <Modal
      open={profile !== null}
      title={profile ? t("margin.edit", { store: profile.name }) : ""}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save")}
      confirmLoading={save.isPending}
      destroyOnHidden
    >
      {profile && (
        <Form<ProfileForm>
          form={form}
          layout="vertical"
          requiredMark={false}
          preserve={false}
          initialValues={{ fee: profile.feeBp / 100, fixedFee: profile.fixedFee, ads: profile.adsBp / 100, margin: profile.marginBp / 100 }}
          onFinish={(v) => save.mutate(v)}
        >
          <Form.Item
            name="fee"
            label={t("margin.fee")}
            extra={
              profile.realFeeBp == null ? (
                t("margin.realFeeNone", { n: formatNumber(profile.settledOrders) })
              ) : (
                <Space>
                  {t("margin.realFeeIs", { pct: `${formatNumber(profile.realFeeBp / 100)}%`, n: formatNumber(profile.settledOrders) })}
                  <Button size="small" type="link" onClick={() => form.setFieldValue("fee", profile.realFeeBp! / 100)}>
                    {t("margin.useReal")}
                  </Button>
                </Space>
              )
            }
            rules={[{ required: true, message: t("field.required") }]}
          >
            <InputNumber<number> {...percent} />
          </Form.Item>
          <Form.Item name="fixedFee" label={t("margin.fixedFee")} extra={t("margin.fixedFeeHelp")}>
            <InputNumber<number> min={0} max={100_000_000_000} precision={0} prefix="Rp" style={{ width: "100%" }} {...rupiahInput} />
          </Form.Item>
          <Form.Item name="ads" label={t("margin.ads")} extra={t("margin.adsHelp")} rules={[{ required: true, message: t("field.required") }]}>
            <InputNumber<number> {...percent} />
          </Form.Item>
          <Form.Item
            name="margin"
            label={t("margin.target")}
            dependencies={["fee", "ads"]}
            rules={[
              { required: true, message: t("field.required") },
              ({ getFieldValue }) => ({
                validator: (_, v: number | undefined) =>
                  bp(getFieldValue("fee")) + bp(getFieldValue("ads")) + bp(v) < 9500 ? Promise.resolve() : Promise.reject(new Error(t("margin.totalTooHigh"))),
              }),
            ]}
          >
            <InputNumber<number> {...percent} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
